/**
 * The wizard's choices and how they move between steps. Kept in sessionStorage so a reload (or a
 * trip to My plans and back in the same tab) keeps them; the screenshot itself stays in memory only.
 */
import type { CalibrationResult } from '../editor/overlay/OverlayCalibrator';
import type { ThemeId } from '../../../shared/types';
import type { LayoutChoice, Picks, SizeId, WaterChoice } from './logic';

/** The six steps of FD §3.2, in order. */
export const STEPS = [
  { id: 'name', label: 'Name' },
  { id: 'land', label: 'Land' },
  { id: 'themes', label: 'Themes' },
  { id: 'animals', label: 'Animals' },
  { id: 'style', label: 'Style' },
  { id: 'layout', label: 'Layout' },
] as const;
export type StepId = (typeof STEPS)[number]['id'];

/** Everything the person chose so far. */
export interface WizardChoices {
  step: number;
  name: string;
  start: 'blank' | 'screenshot';
  sizeId: SizeId;
  themes: ThemeId[];
  picks: Picks;
  /** The themes and land the current picks were made for; new defaults are worked out when it changes. */
  picksFor: string;
  /** The person changed a tick or a count, so their picks are kept (and only new animals get defaults). */
  picksTouched: boolean;
  layout: LayoutChoice;
  water: WaterChoice;
}

/** A screenshot start: the shrunk picture and, once lined up, where it sits on the map. */
export interface Screenshot {
  blob: Blob;
  url: string;
  calibration: CalibrationResult | null;
}

/** A fresh wizard. */
export function initialChoices(): WizardChoices {
  return {
    step: 0,
    name: '',
    start: 'blank',
    sizeId: 'medium',
    themes: [],
    picks: {},
    picksFor: '',
    picksTouched: false,
    layout: 'surprise',
    water: 'surprise',
  };
}

const KEY = 'zp.wizard';

/** The choices kept from earlier in this tab, or a fresh wizard. */
export function loadChoices(): WizardChoices {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) {
      const c = { ...initialChoices(), ...(JSON.parse(raw) as Partial<WizardChoices>) };
      // The screenshot isn't kept, so a screenshot start goes back to the land step to pick it again.
      if (c.start === 'screenshot' && c.step > 1) c.step = 1;
      return c;
    }
  } catch {
    // ignore: start fresh
  }
  return initialChoices();
}

/** Remembers the choices for this tab. */
export function storeChoices(c: WizardChoices): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(c));
  } catch {
    // ignore
  }
}

/** Forgets the choices (after the plan was made). */
export function clearChoices(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
